import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TableConfigDto,
  TableRowDto,
} from '@asset-metrix/portal-gateway-client-js';
import { AsyncNotification } from '@asset-metrix/portal-ui';
import { useDisclosure } from '@mantine/hooks';

import { useUpdateTableRow } from '../../hooks/useUpdateTableRow';
import { invalidateTable } from '../../utils/queryCacheHelper';

import {
  isInvestmentAssumptionForm,
  transformRowData,
} from './investmentAssumptionModalHelpers';
import { UpdateTableRowModalProps } from './UpdateTableRowModal';

export type RowData = {
  rowId: string;
  data: TableRowDto['data'];
};

type State = {
  rows: RowData[];
};

export type UseUpdateTableRowModalPropertiesReturnValue = {
  updateTableRowModalProps?: UpdateTableRowModalProps;
  open: (data: RowData[]) => void;
};

export function useUpdateTableRowModalProperties(
  pageId: string,
  contentId: string,
  config: TableConfigDto,
  instanceId: string
): UseUpdateTableRowModalPropertiesReturnValue {
  const [isOpen, { open, close }] = useDisclosure();
  const [state, setState] = useState<State | null>(null);
  const { t } = useTranslation();

  const invalidateTableCache = () => {
    void invalidateTable(config.cacheKey);
  };

  const mutationSuccessHandler = () => {
    invalidateTableCache();
    setState(null);
    resetUpdateRow();
    close();
    AsyncNotification.show(
      { message: t('app.table.workflowActionModal.successMessage') },
      'success'
    );
  };

  const {
    updateTableRows,
    status: updateRowStatus,
    error,
    reset: resetUpdateRow,
  } = useUpdateTableRow({
    mutationSuccessHandler,
    refreshTableAfterPartialBulkUpdateError: invalidateTableCache,
  });

  const updateTableRowModalProps = useMemo<
    UpdateTableRowModalProps | undefined
  >(() => {
    if (state === null || !config.updateJsonSchema) {
      return undefined;
    }

    const resetStateAndClose = () => {
      setState(null);
      resetUpdateRow();
      close();
    };

    const updateSchema = config.updateJsonSchema;
    if (isInvestmentAssumptionForm(config.createRowConfig?.type)) {
      // patch enum property for conditional Filters in addon investments
      // otherwise the initial values get lost
      // we only allow one row in the investment assumption modal, pick the value from it's data
      const data = state.rows[0];
      if (data != null) {
        // In this path rows are transformed by transformRowData() so reference has this shape
        const ref = data.data.reference as {
          investmentCurrency?: unknown;
          financingSource?: unknown;
          loanId?: unknown;
        };
        const schema = updateSchema as {
          properties: {
            reference: { properties: Record<string, { enum?: unknown[] }> };
          };
        };
        schema.properties.reference.properties.investmentCurrency.enum = [
          ref.investmentCurrency,
        ];
        if (config.createRowConfig?.type === 'addonInvestmentRow') {
          schema.properties.reference.properties.financingSource.enum = [
            ref.financingSource,
          ];
          schema.properties.reference.properties.loanId.enum = [
            null,
            ref.loanId, // loanId is optional -> add null to initial enum
          ];
        }
      }
    }

    return {
      contentId,
      instanceId,
      data: state.rows,
      errorMessage: error?.message,
      jsonSchema: updateSchema,
      schemaType: config.createRowConfig?.type ?? 'row',
      onApply: (data, context) => {
        updateTableRows(
          contentId,
          state.rows.map(({ rowId }) => rowId),
          {
            data,
            context,
          }
        );
      },
      onClose: resetStateAndClose,
      opened: isOpen,
      updateRowStatus,
    };
  }, [
    state,
    config.createRowConfig,
    config.updateJsonSchema,
    contentId,
    error?.message,
    isOpen,
    updateRowStatus,
    resetUpdateRow,
    close,
    updateTableRows,
    instanceId,
  ]);

  const openCallback = useCallback(
    (
      rowData: {
        rowId: string;
        file_name?: string;
        data: TableRowDto['data'];
      }[]
    ) => {
      setState({
        rows: isInvestmentAssumptionForm(config.createRowConfig?.type)
          ? transformRowData(rowData)
          : rowData,
      });
      open();
    },
    [open, config.createRowConfig]
  );

  return {
    open: openCallback,
    updateTableRowModalProps,
  };
}
